import { useEffect, useRef } from "react";

type Direction = "up" | "left" | "right" | "none";

interface ScrollRevealOptions {
  threshold?: number;
  delay?: number;
  direction?: Direction;
  distance?: number;
  duration?: number;
}

const getTransform = (direction: Direction, distance: number): string => {
  switch (direction) {
    case "left":
      return `translateX(-${distance}px)`;
    case "right":
      return `translateX(${distance}px)`;
    case "none":
      return "none";
    case "up":
    default:
      return `translateY(${distance}px)`;
  }
};

export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options: ScrollRevealOptions = {}
) {
  const {
    threshold = 0.12,
    delay = 0,
    direction = "up",
    distance = 28,
    duration = 700,
  } = options;
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.style.opacity = "0";
    el.style.transform = getTransform(direction, distance);
    el.style.transition = `opacity ${duration}ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = "1";
          el.style.transform = direction === "none" ? "none" : "translate(0,0)";
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, delay, direction, distance, duration]);

  return ref;
}

/** Animates a horizontal line from 0 → 100% width when it enters view */
export function useDrawLine<T extends HTMLElement = HTMLElement>(
  options: { delay?: number; duration?: number } = {}
) {
  const { delay = 0, duration = 900 } = options;
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.style.width = "0%";
    el.style.transition = `width ${duration}ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.width = "100%";
          observer.unobserve(el);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [delay, duration]);

  return ref;
}